import React from 'react';
import {
  Typography,
  Box,
  Button,
  Grid,
  Paper,
  Divider,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../../app/utils/formatters';
import { Order } from '../../../app/data/models/orders/order';
import PaymentIcon from '@mui/icons-material/Payment';
import SummarizeIcon from '@mui/icons-material/Summarize';
import Restaurant from '@app/data/models/restaurant';

interface PaymentSummaryProps {
  order: Order;
  restaurant?: Restaurant | null;
  totalPaid: number;
  totalRefunded: number;
  netPaid: number;
  remainingAmount: number;
  isFullyPaid: boolean;
  onOpenPaymentDialog: () => void;
  onPrintConsolidatedReceipt: () => void;
}

const PaymentSummary: React.FC<PaymentSummaryProps> = ({
  order,
  restaurant,
  totalPaid,
  totalRefunded,
  netPaid,
  remainingAmount,
  isFullyPaid,
  onOpenPaymentDialog,
  onPrintConsolidatedReceipt,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Grid item xs={12} md={6}>
      <Typography variant="h6" gutterBottom>
        {t('biller.payment-summary')}
      </Typography>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body1">{t('biller.subtotal')}</Typography>
          <Typography variant="body1">
            {formatCurrency(order.subtotal)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body1">
            {t('biller.tax')}{' '}
            {restaurant?.settings?.priceIncludesTax &&
              `(${t('biller.tax-inclusive')})`}
          </Typography>
          <Typography variant="body1">
            {formatCurrency(order.taxAmount)}
          </Typography>
        </Box>
        {order.deliveryFee > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body1">{t('biller.delivery-fee')}</Typography>
            <Typography variant="body1">
              {formatCurrency(order.deliveryFee)}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body1" fontWeight="bold">
            {t('biller.total')}
          </Typography>
          <Typography variant="body1" fontWeight="bold">
            {formatCurrency(order.total)}
          </Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body1">{t('biller.amount-paid')}</Typography>
          <Typography variant="body1">{formatCurrency(totalPaid)}</Typography>
        </Box>
        {totalRefunded > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body1" color="error.main">
              {t('biller.refunded')}
            </Typography>
            <Typography variant="body1" color="error.main">
              {formatCurrency(totalRefunded)}
            </Typography>
          </Box>
        )}
        {totalRefunded > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body1" fontWeight="bold">
              {t('biller.net-paid')}
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {formatCurrency(netPaid)}
            </Typography>
          </Box>
        )}
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body1" fontWeight="bold">
            {t('biller.remaining-to-pay')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="body1"
              fontWeight="bold"
              color={remainingAmount > 0 ? 'error.main' : 'success.main'}
            >
              {formatCurrency(remainingAmount)}
            </Typography>
            {isFullyPaid && (
              <Chip
                size="small"
                color="success"
                label={t('biller.paid')}
                sx={{ ml: 1 }}
              />
            )}
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            startIcon={<PaymentIcon />}
            onClick={onOpenPaymentDialog}
            disabled={isFullyPaid}
            fullWidth={isMobile}
          >
            {t('biller.collect-payment')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<SummarizeIcon />}
            onClick={onPrintConsolidatedReceipt}
            disabled={!order.payments?.length}
            fullWidth={isMobile}
          >
            {t('biller.print-receipt')}
          </Button>
        </Box>
      </Paper>
    </Grid>
  );
};

export default PaymentSummary;
